import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getBorrowRecords,
  createBorrowRecord,
  updateBorrowRecord,
  deleteBorrowRecord,
  getBorrowRecordById,
} from '../services/borrowRecordService';
import Notification from '../components/Common/Notification';

// Create and export the context
export const BorrowRecordContext = createContext();

// Context Provider component
export const BorrowRecordProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [borrowRecords, setBorrowRecords] = useState([]);
  const [selectedBorrowRecord, setSelectedBorrowRecord] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  // Function to fetch authentication headers
  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  // Function to show notifications
  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  // Function to close notifications
  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  // Function to fetch all borrow records
  const fetchAllBorrowRecords = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getBorrowRecords(authHeaders);
      setBorrowRecords(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching borrow records.';
      showNotification(errorMessage, true);
    }
  };

  // Function to fetch a borrow record by ID
  const fetchBorrowRecordById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getBorrowRecordById(id, authHeaders);
      setSelectedBorrowRecord(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching borrow record with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  // Function to add a new borrow record
  const addBorrowRecord = async (borrowRecordData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newBorrowRecord = await createBorrowRecord(borrowRecordData, authHeaders);
      setBorrowRecords(prevRecords => [...prevRecords, newBorrowRecord]);
      showNotification('Borrow record added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding borrow record.';
      showNotification(errorMessage, true);
    }
  };

  // Function to edit an existing borrow record
  const editBorrowRecord = async (id, borrowRecordData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedBorrowRecord = await updateBorrowRecord(id, borrowRecordData, authHeaders);
      setBorrowRecords(prevRecords =>
        prevRecords.map(record => (record.id === id ? updatedBorrowRecord : record))
      );
      showNotification('Borrow record updated successfully!', false);
      return updatedBorrowRecord;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating borrow record.';
      showNotification(errorMessage, true);
      throw error;
    }
  };

  // Function to remove a borrow record
  const removeBorrowRecord = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteBorrowRecord(id, authHeaders);
      setBorrowRecords(prevRecords => prevRecords.filter(record => record.id !== id));
      showNotification('Borrow record removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing borrow record.';
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllBorrowRecords();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <BorrowRecordContext.Provider
      value={{
        borrowRecords,
        selectedBorrowRecord,
        fetchAllBorrowRecords,
        fetchBorrowRecordById,
        addBorrowRecord,
        editBorrowRecord,
        removeBorrowRecord,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </BorrowRecordContext.Provider>
  );
};
